import * as React from 'react';

import {
  CurrencyCode,
  enumCurrencyCode,
  enumUnit,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { convertFromPounds, weightToPounds } from '@rematter/ui-web';
import Big from 'big.js';

import {
  BrokeredLoadFormLineItem,
  enumBrokeredLoadWeightType,
} from '../types/brokeredLoad';

import { WeightUnitType } from '~/modules/trade/types/load';
import {
  convertWeightForPricing,
  getCommonPricingUnit,
  LineItemForTotals,
} from '~/modules/trade/utils/weight';

type BrokeredLoadLineItemsTotals = {
  materialCount: number;
  totalGross: Weight | null;
  totalTare: Weight | null;
  totalNet: Weight | null;
  totalValue: number;
  currencyCode: CurrencyCode;
  displayUnit: Unit;
};

const sumWeightsInPounds = (
  lineItems: BrokeredLoadFormLineItem[],
  weightType: enumBrokeredLoadWeightType,
): number =>
  lineItems
    .reduce(
      (sum, item) => sum.plus(weightToPounds(item.weights?.[weightType])),
      new Big(0),
    )
    .toNumber();

const toWeight = (valueInPounds: number, displayUnit: Unit): Weight | null => {
  if (valueInPounds <= 0) return null;

  return {
    value: convertFromPounds(valueInPounds, displayUnit),
    unit: displayUnit,
    __typename: 'Weight' as const,
  };
};

export const useBrokeredLoadLineItemsTotals = (
  lineItems: BrokeredLoadFormLineItem[],
  weightUnit?: WeightUnitType,
): BrokeredLoadLineItemsTotals =>
  React.useMemo(() => {
    const items: LineItemForTotals[] = lineItems.map((li) => ({
      quantity: li.weights?.[enumBrokeredLoadWeightType.Net]?.value ?? null,
      unit:
        li.weights?.[enumBrokeredLoadWeightType.Net]?.unit ??
        li.pricingUnit ??
        null,
    }));

    const commonUnit = getCommonPricingUnit(items, enumUnit.Pounds);
    const displayUnit = weightUnit === 'scale' ? enumUnit.Pounds : commonUnit;

    const grossInPounds = sumWeightsInPounds(
      lineItems,
      enumBrokeredLoadWeightType.Gross,
    );
    const tareInPounds = sumWeightsInPounds(
      lineItems,
      enumBrokeredLoadWeightType.Tare,
    );
    const netInPounds = sumWeightsInPounds(
      lineItems,
      enumBrokeredLoadWeightType.Net,
    );

    const totalValue = lineItems
      .reduce((sum, item) => {
        const netPounds = weightToPounds(
          item.weights?.[enumBrokeredLoadWeightType.Net],
        );
        const pricingUnit = item.pricingUnit ?? enumUnit.Pounds;
        const convertedWeight = convertWeightForPricing(netPounds, pricingUnit);
        return sum.plus(
          new Big(item.unitPrice?.amount ?? 0).times(convertedWeight),
        );
      }, new Big(0))
      .toNumber();

    const currencyCode =
      lineItems.find((item) => item.unitPrice?.currencyCode)?.unitPrice
        ?.currencyCode ?? enumCurrencyCode.USD;

    return {
      materialCount: lineItems.length,
      totalGross: toWeight(grossInPounds, displayUnit),
      totalTare: toWeight(tareInPounds, displayUnit),
      totalNet: toWeight(netInPounds, displayUnit),
      totalValue,
      currencyCode,
      displayUnit,
    };
  }, [lineItems, weightUnit]);
